import { type ComponentProps, useMemo } from "react";
import type { RoleOption } from "../../shared/rbac/roles";
import { Dropdown } from "./Dropdown";
import { Icon } from "./Icon";
import { PillBadge } from "./Settings/shared";

type Props = {
  roleOptions: RoleOption[];
  selected: RoleOption[];
  onChange: (selected: RoleOption[]) => void;
  label?: string;
  align?: ComponentProps<typeof Dropdown>["align"];
};

export function RoleMultiSelect({
  roleOptions,
  selected,
  onChange,
  label,
  align = "bottom-left",
}: Props) {
  const selectedKeys = useMemo(
    () => new Set(selected.map((r) => r.builtinRoleId ?? r.customRoleId ?? "")),
    [selected]
  );

  const toggle = (opt: RoleOption) => {
    const key = opt.builtinRoleId ?? opt.customRoleId ?? "";
    if (selectedKeys.has(key)) {
      onChange(
        selected.filter(
          (r) => (r.builtinRoleId ?? r.customRoleId ?? "") !== key
        )
      );
    } else {
      onChange([...selected, opt]);
    }
  };

  return (
    <Dropdown
      opener={
        <PillBadge
          icon="id-card-lanyard"
          trailingIcon="chevron-down"
          interactive
        >
          {label ??
            (selected.length === 0
              ? "Choose roles"
              : selected.length === 1
                ? selected[0]!.label
                : `${selected.length} roles`)}
        </PillBadge>
      }
      align={align}
      minWidth={160}
      sections={[
        {
          items: roleOptions.map((opt) => {
            const key = opt.builtinRoleId ?? opt.customRoleId ?? "";
            return {
              type: "button" as const,
              keepOpen: true,
              left: <Icon name="id-card-lanyard" size={16} />,
              label: opt.label,
              right: selectedKeys.has(key) ? (
                <Icon name="check" size={12} color="var(--text-secondary)" />
              ) : undefined,
              onClick: () => toggle(opt),
            };
          }),
        },
      ]}
    />
  );
}
